<template xmlns:v-slot="http://www.w3.org/1999/XSL/Transform">
  <v-navigation-drawer
    v-model="showDrawer"
    :width="getNavWidth()"
    :height="getNavHeight()"
    right
    absolute
    temporary
    clipped
    overflow
    app
  >
    <SideContent>
      <template v-slot:header>
        <button :class="$style.pastOrdersClose">
          Past orders
          <span :class="$style.upButton">
            <svg v-svg
                 :class="$style.chevronDownIcon"
                 symbol="chevron-down" />
          </span>
        </button>
      </template>
      <template v-slot:main>
        <div v-if="!isKioskPlatform && user"
             :class="$style.pastOrdersWelcome">
          <h4 :class="$style.welcomeTitle">
            Welcome Back, {{ user.firstName }}!
          </h4>
          <p>Here’s your last 5 orders</p>
        </div>

        <div :class="$style.pastOrdersTitle">
          <h4><span>MY PAST ORDER</span>S</h4>
          <p>Up to 5 past orders</p>
        </div>

        <ul :class="$style.ordersList">
          <Order v-for="(order, index) in lastFiveOrders"
                 :key="index"
                 :order="order"
                 @save-to-cart="saveToCart"/>
        </ul>
      </template>
      <template v-slot:footer>
        <KioskTryBlock v-if="isKioskPlatform" />
      </template>
    </SideContent>
  </v-navigation-drawer>
</template>

<script>
import Component from 'vue-class-component';
import { Action, Getter } from 'vuex-class';
import Vue from 'vue';
import SideContent from '@/layouts/SideContent';
import KioskTryBlock from '@/components/KioskTryBlock';
import Order from '@/apps/cart/components/Order';
import { CART_SAVE_ORDER, CART_DISPLAY, ORDERS_LOAD } from '@/apps/cart/store/types';

/**
 * Provide navigation drawer setup for past orders. Renders orders in the
 * main slot of the SideContext component.
 *
 * DATA
 * ============================================================================
 * @prop array orders - array with previous order data.
 */
@Component({
  name:       'PastOrders',
  components: {
    SideContent,
    Order,
    KioskTryBlock,
  },
  props: {
    value: {
      type:     Boolean,
      default:  false,
      required: false,
    },
  },
})
export default class PastOrders extends Vue {
  @Getter('authUser') user;
  @Getter('orders') orders;
  @Getter isKioskPlatform;
  @Action(CART_SAVE_ORDER) saveOrderToCart;
  @Action(CART_DISPLAY) cartDisplay;
  @Action(ORDERS_LOAD) loadOrders;

  /**
   * Get last 5 orders for current user (sorted by date).
   */
  get lastFiveOrders() {
    const sortedOrders = this.orders
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    if (sortedOrders.length > 5) {
      return sortedOrders.slice(sortedOrders.length - 5);
    }

    return sortedOrders;
  }

  get showDrawer() {
    return this.value;
  }
  set showDrawer(value) {
    if (!value) {
      this.$emit('closeDrawer', value);
    }
  }

  @Getter itemByKey;
  /* Window width starting value */
  windowWidth = window.innerWidth;

  /* PastOrders starting value */
  ordersHeight = window.innerHeight - 70;

  saveToCart(order) {
    this.saveOrderToCart(order);

    this.showDrawer = false;
    this.cartDisplay(true);
  }

  /**
   * Create vue hook.
   */
  created() {
    this.loadOrders();
  }

  /**
   * Listens window width change
   * and updates isFullscreen result if necessary
   */
  mounted() {
    window.addEventListener('resize', this.getNavWidth);
    window.addEventListener('resize', this.getNavHeight);
  }

  beforeDestroy() {
    window.removeEventListener('resize', this.getNavWidth);
    window.removeEventListener('resize', this.getNavHeight);
  }

  /**
   * Gets window width and returns 340 for desktop screens
   * or window width itself for medium and small screens
   * where navigation-drawer width should be fullscreen
   */
  getNavWidth() {
    this.windowWidth = window.innerWidth;
    if (this.windowWidth > 1080) {
      return 340;
    }

    return this.windowWidth;
  }

  /**
   * Updates full height of the nav sidebar
   */
  getNavHeight() {
    if (this.isKioskPlatform) {
      this.ordersHeight = window.innerHeight - 115;
    } else {
      this.ordersHeight = window.innerHeight - 70;
    }

    return this.ordersHeight;
  }
}
</script>

<style lang="scss" module>
@import '@/style/variables.scss';
@import '@/style/helpers.scss';

.nav {
  overflow-y: auto;
}

.pastOrdersClose {
  width: 100%;
  box-shadow: 0 1px 2px 0 rgba(125, 125, 125, 0.37);
  padding: 13px;
  font-family: 'Dekers', sans-serif;
  font-size: 15px;
  font-weight: 700;
  line-height: 18px;
  letter-spacing: -0.08px;
  position: relative;
  background-color: $white;
  outline: none;
  span {
    position: absolute;
    right: 19px;
    top: calc(50% - 5px);
  }
}

.pastOrdersTitle {
  padding-top: 76px;
  padding-bottom: 60px;
  text-align: center;
  display: none;
  h4 {
    @extend %main-title;
    font-size: 48px;
    line-height: 44px;
    margin-bottom: 10px;
    span::before,
    span::after {
      width: 120px;
      border-top: 7px solid $green-color;
    }
    span::before {
      left: -200px;
    }
    span::after {
      right: -200px;
    }
  }
  p {
    margin-bottom: 0;
    font-size: 34px;
    font-weight: 300;
    line-height: 48px;
  }
}

.chevronDownIcon {
  fill: $black;
  width: 12px;
  height: 12px;
  transform: rotateX(180deg);
}

.pastOrdersWelcome {
  padding: 31px 10px 28px;
  text-align: center;
  h4 {
    font-family: 'LP Bambus', sans-serif;
    font-weight: 400;
    font-size: 27px;
    color: $green-color;
    letter-spacing: 0.75px;
    line-height: 22px;
    margin-bottom: 8px;
  }
  p {
    font-style: italic;
    color: $black-semi-transparent;
    line-height: 15px;
  }
}

.ordersList {
  list-style: none;
  padding-left: 0;
  margin-bottom: 0;
}

@media screen and (max-width: $kiosk-breakpoint-max) {
  .pastOrdersClose {
    display: none;
  }
  .pastOrdersWelcome {
    display: none;
  }
  .pastOrdersTitle {
    display: block;
  }
}

@media screen and (max-width: 960px) {
  .pastOrdersWelcome {
    display: block;
  }
  .pastOrdersTitle {
    display: none;
  }
}
</style>



// WEBPACK FOOTER //
// src/apps/cart/components/PastOrders.vue